import { ld } from "@/lib/theme/light-dark";
import {
  Building2,
  ChevronDown,
  ChevronUp,
  Contact,
  GripVertical,
  PenLine,
  Plus,
  Trash2,
} from "lucide-react";
/**
 * Builder canvas for the Formulare module — the "Seite 1" card holding the
 * Übergabe form (Liegenschaft / Kontakt / Unterschrift fields + Absenden) and
 * the "Neue Seite" placeholder. 1:1 with the prototype (`isFormulare` content,
 * source lines 1488–1524).
 */
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";

/** A draggable field row: grip handle + an accent picker pill. */
function PickerField(props: {
  readonly label: string;
  readonly icon: ReactNode;
  readonly value: string;
  readonly marginTop?: number;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        marginTop: props.marginTop,
      }}
    >
      <span
        style={{
          color: ld("rgba(11,15,25,0.2)", "rgba(250,250,250,0.2)"),
          display: "inline-flex",
          marginTop: 25,
          cursor: "grab",
        }}
      >
        <GripVertical width={15} height={15} strokeWidth={1.9} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: H.ink,
            marginBottom: 6,
          }}
        >
          {props.label} <span style={{ color: ld("#C0453E", "#ff7e73") }}>*</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            background: H.accentSoft,
            border: `1px solid ${H.accentBorder}`,
            borderRadius: 9,
            padding: "9px 11px",
          }}
        >
          <span style={{ color: H.accent, display: "inline-flex" }}>
            {props.icon}
          </span>
          <span style={{ fontSize: 13, fontWeight: 500, color: H.accent }}>
            {props.value}
          </span>
        </div>
      </div>
    </div>
  );
}

/** The full builder canvas (page header + form card + "Neue Seite"). */
export function ModuleFormulareCanvas(): ReactNode {
  const t = useTranslations("marketing.moduleFormulareCanvas");
  return (
    <div
      style={{ flex: 1, minWidth: 0, overflow: "hidden", padding: "17px 20px" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <span
          style={{
            width: 25,
            height: 25,
            borderRadius: "50%",
            background: H.accent,
            color: ld("#fff", "#181b1f"),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          1
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, color: H.ink }}>
          {t("seite1")}
        </span>
      </div>

      <div
        style={{
          maxWidth: 400,
          background: ld("#fff", "#181b1f"),
          border: `1px solid ${ld("#EAEAE5", "#323539")}`,
          borderLeft: `3px solid ${H.accent}`,
          borderRadius: 13,
          boxShadow: `0 16px 36px -26px ${ld("rgba(11,15,25,0.24)", "rgba(0,0,0,0.41)")}`,
          padding: "18px 20px",
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: H.ink,
            marginBottom: 15,
          }}
        >
          {t("uebergabe")}
        </div>

        <PickerField
          label={t("liegenschaft")}
          icon={<Building2 width={16} height={16} strokeWidth={1.9} />}
          value={t("liegenschaftAuswaehlen")}
        />
        <PickerField
          label={t("kontakt")}
          icon={<Contact width={16} height={16} strokeWidth={1.9} />}
          value={t("kontaktAuswaehlen")}
          marginTop={13}
        />

        {/* Unterschrift field (active grip, header controls, sign pad) */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            marginTop: 13,
          }}
        >
          <span
            style={{
              color: H.accent,
              display: "inline-flex",
              marginTop: 25,
              cursor: "grab",
            }}
          >
            <GripVertical width={15} height={15} strokeWidth={1.9} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 12.5, fontWeight: 600, color: H.ink }}>
                {t.rich("unterschriftPflicht", {
                  req: (chunks: ReactNode): ReactNode => (
                    <span style={{ color: ld("#C0453E", "#ff7e73") }}>{chunks}</span>
                  ),
                })}
              </span>
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    border: `1px solid ${ld("#EAEAE5", "#323539")}`,
                    borderRadius: 6,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
                  }}
                >
                  <ChevronUp width={13} height={13} strokeWidth={1.9} />
                </span>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    border: `1px solid ${ld("#EAEAE5", "#323539")}`,
                    borderRadius: 6,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
                  }}
                >
                  <ChevronDown width={13} height={13} strokeWidth={1.9} />
                </span>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    border: `1px solid ${ld("#F1DAD8", "#4b3736")}`,
                    background: ld("#FCF4F3", "#25282c"),
                    borderRadius: 6,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: ld("#C0453E", "#ff7e73"),
                  }}
                >
                  <Trash2 width={13} height={13} strokeWidth={1.9} />
                </span>
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                border: `1px dashed ${ld("#D6D6CF", "#43464b")}`,
                borderRadius: 9,
                height: 58,
                color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
                fontSize: 12.5,
              }}
            >
              <PenLine width={15} height={15} strokeWidth={1.8} />
              {t("unterschreibenTippen")}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 17,
            paddingLeft: 23,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 34,
              padding: "0 16px",
              border: `1px solid ${H.accentBorder}`,
              borderRadius: 9,
              color: H.accent,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {t("absenden")}
          </span>
        </div>
      </div>

      <div
        style={{
          maxWidth: 400,
          marginTop: 11,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
          border: `1px dashed ${ld("#D2D2CB", "#474a4e")}`,
          borderRadius: 11,
          padding: 10,
          color: H.inkDim,
          fontSize: 12.5,
          cursor: "pointer",
        }}
      >
        <Plus width={15} height={15} strokeWidth={1.9} />
        {t("neueSeite")}
      </div>
    </div>
  );
}
